import type { RouteLocationNormalized } from "vue-router";
import esquireClient from "@/types/clients/esquire";
import { useSessionStore } from "@/stores/session";

export type CheckinFlowStep =
  | "login"
  | "passport"
  | "guest_info"
  | "options"
  | "overview"
  | "payment"
  | "complete_checkin";

export type CheckinFlowEvent =
  | "login_completed"
  | "unit_selected"
  | "step_viewed"
  | "step_left"
  | "passport_method_selected"
  | "passport_upload_started"
  | "passport_upload_completed"
  | "passport_upload_failed"
  | "passport_manual_submitted"
  | "passport_manual_completed"
  | "passport_manual_failed"
  | "passport_confirmed"
  | "passport_rejected"
  | "passport_skipped"
  | "passport_skip_failed"
  | "vat_validation_started"
  | "vat_validation_completed"
  | "vat_validation_failed"
  | "guest_info_submitted"
  | "guest_info_completed"
  | "guest_info_failed"
  | "option_changed"
  | "breakfast_dates_confirmed"
  | "options_completed"
  | "terms_accepted"
  | "payment_started"
  | "payment_start_failed"
  | "payment_returned"
  | "checkin_completed"
  | "checkin_completion_failed";

type CheckinFlowMetadataValue = string | number | boolean | null | undefined;
export type CheckinFlowMetadata = Record<string, CheckinFlowMetadataValue>;

export type TrackCheckinFlowEventArgs = {
  step: CheckinFlowStep;
  event: CheckinFlowEvent;
  routeName?: string | null;
  previousRouteName?: string | null;
  durationMs?: number | null;
  errorCode?: string | null;
  metadata?: CheckinFlowMetadata;
};

type TrackCheckinFlowPayload = {
  reservation_id: string;
  contract_id: string | null;
  step: CheckinFlowStep;
  event: CheckinFlowEvent;
  occurred_at: string;
  route_name: string | null;
  previous_route_name: string | null;
  duration_ms: number | null;
  error_code: string | null;
  metadata: Record<string, string | number | boolean | null>;
};

type RouteTrackingInfo = {
  step: CheckinFlowStep;
  event: CheckinFlowEvent;
};

const TRACKING_ROUTE_KEY = "track-checkin-flow";

const ROUTE_TRACKING: Record<string, RouteTrackingInfo> = {
  "passport-scanner": { step: "passport", event: "step_viewed" },
  checkin: { step: "guest_info", event: "step_viewed" },
  "reservation-options": { step: "options", event: "step_viewed" },
  "reservation-overview": { step: "overview", event: "step_viewed" },
  "reservation-confirmation": { step: "payment", event: "step_viewed" }
};

class CheckinFlowTrackingService {
  private activeRouteName: string | null = null;
  private activeRouteStartedAt: number | null = null;
  private lastRouteViewKey: string | null = null;

  public trackRouteView(to: RouteLocationNormalized, from: RouteLocationNormalized): void {
    const now = Date.now();
    const fromName = this.routeName(from);

    if (fromName && this.activeRouteName === fromName && this.activeRouteStartedAt) {
      const fromTracking = ROUTE_TRACKING[fromName];

      if (fromTracking) {
        void this.track({
          step: fromTracking.step,
          event: "step_left",
          routeName: fromName,
          previousRouteName: this.routeName(to),
          durationMs: now - this.activeRouteStartedAt
        });
      }
    }

    const toName = this.routeName(to);
    const toTracking = toName ? ROUTE_TRACKING[toName] : null;

    this.activeRouteName = toName;
    this.activeRouteStartedAt = now;

    if (!toName || !toTracking) return;

    const routeViewKey = `${toName}:${to.fullPath}`;
    if (routeViewKey === this.lastRouteViewKey) return;

    this.lastRouteViewKey = routeViewKey;

    void this.track({
      step: toTracking.step,
      event: toTracking.event,
      routeName: toName,
      previousRouteName: fromName,
      metadata: {
        path: to.path
      }
    });
  }

  public async track(args: TrackCheckinFlowEventArgs): Promise<void> {
    const url = this.trackingUrl();
    const payload = this.createPayload(args);

    if (!url || !payload) return;

    try {
      await esquireClient.fetch(() => new URL(url), {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload),
        keepalive: true
      });
    } catch {
      // Tracking should never block or break the check-in flow.
    }
  }

  private routeName(route: RouteLocationNormalized): string | null {
    return route.name ? String(route.name) : null;
  }

  private trackingUrl(): string | null {
    const session = useSessionStore();
    return session.reservation?.routes?.[TRACKING_ROUTE_KEY] ?? null;
  }

  private createPayload(args: TrackCheckinFlowEventArgs): TrackCheckinFlowPayload | null {
    const session = useSessionStore();
    const reservation = session.reservation;

    if (!reservation?.id) return null;

    return {
      reservation_id: reservation.id,
      contract_id: session.contract?.id ?? session.contractId ?? null,
      step: args.step,
      event: args.event,
      occurred_at: new Date().toISOString(),
      route_name: args.routeName ?? null,
      previous_route_name: args.previousRouteName ?? null,
      duration_ms: args.durationMs ?? null,
      error_code: args.errorCode ?? null,
      metadata: this.cleanMetadata(args.metadata)
    };
  }

  private cleanMetadata(metadata?: CheckinFlowMetadata): Record<string, string | number | boolean | null> {
    if (!metadata) return {};

    return Object.entries(metadata).reduce<Record<string, string | number | boolean | null>>((acc, [key, value]) => {
      if (value === undefined) return acc;

      acc[key] = value;
      return acc;
    }, {});
  }
}

export default new CheckinFlowTrackingService();
